<script setup>
import { ref, onBeforeMount, onBeforeUnmount, computed } from "vue";
import { useStore } from "vuex";
import authService from "@/services/auth.service";

import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import PasswordInput from "@/components/PasswordInput.vue";
import PasswordValidator from "@/components/PasswordValidator.vue";
import ErrorAlert from "@/components/ErrorAlert.vue";
import PlanSelector from "@/components/PlanSelector.vue";

const body = document.getElementsByTagName("body")[0];
const store = useStore();

// Estado del formulario
const currentStep = ref(0); // Empezar en 0 para selección de plan
const planCode = ref(""); // Campo obligatorio para el plan seleccionado
const billingCycle = ref("monthly"); // monthly o yearly
const userType = ref("");
const error = ref(null);
const successMessage = ref(null);
const isLoading = ref(false);
const passwordValidatorRef = ref(null);
const showForm = ref(true); // Controla si se muestra el formulario
const fieldErrors = ref({});

// Datos básicos del usuario
const name = ref("");
const email = ref("");
const password = ref("");
const passwordConfirmation = ref("");

// Datos del médico
const doctorRole = ref("");
const selectedSpecialties = ref([]);
const doctorPhone = ref("");
const doctorEmailContact = ref("");

// Datos del centro médico
const centerName = ref("");
const centerAddress = ref("");

// Datos para selects
const doctorRoles = ref([]);
const specialties = ref([]);
const acceptTerms = ref(false);
const centerInfoComplete = computed(() => centerName.value && centerAddress.value);

const doctorInfoComplete = computed(
  () =>
    doctorRole.value &&
    (isGeneralDoctorRole() || selectedSpecialties.value.length > 0) &&
    doctorPhone.value
);
const totalSteps = computed(() => 4);
const isLastStep = computed(() => currentStep.value === totalSteps.value - 1);
const isDoctorInfoStep = computed(
  () => userType.value === "doctor" && currentStep.value === 3
);
const isCenterInfoStep = computed(
  () => userType.value !== "doctor" && currentStep.value === 3
);
const progressPercentage = computed(() => {
  if (totalSteps.value <= 1) return 0;
  return Math.round((currentStep.value / (totalSteps.value - 1)) * 100);
});
const selectedPlanName = computed(() => {
  const planNames = {
    'esencial': 'Plan Esencial',
    'profesional': 'Plan Profesional',
    'clinica': 'Plan Clínica'
  };
  return planCode.value ? planNames[planCode.value] || 'Plan seleccionado' : 'Selecciona plan';
});

const userTypeLabel = computed(() => {
  if (!userType.value) return 'Tipo de usuario';
  return userType.value === 'doctor' ? 'Médico' : 'Director Médico';
});

const stepMeta = computed(() => {
  const isDoctor = userType.value === "doctor";
  return [
    {
      key: "plan",
      title: "Plan",
      subtitle: selectedPlanName.value,
      icon: "fas fa-layer-group"
    },
    {
      key: "profile",
      title: "Perfil",
      subtitle: userTypeLabel.value,
      icon: "fas fa-user-tag"
    },
    {
      key: "account",
      title: "Cuenta",
      subtitle: "Datos personales",
      icon: "fas fa-id-card"
    },
    {
      key: "details",
      title: isDoctor ? "Profesional" : "Centro",
      subtitle: isDoctor ? "Especialidad y centro" : "Datos del centro",
      icon: isDoctor ? "fas fa-user-md" : "fas fa-hospital"
    }
  ];
});

const toggleDefaultLayout = () => store.commit("toggleDefaultLayout");

onBeforeMount(async () => {
  store.commit("hideConfigButton", true);
  toggleDefaultLayout();
  body.classList.remove("bg-gray-100");

  // Cargar datos iniciales
  await loadInitialData();
});

onBeforeUnmount(() => {
  store.commit("hideConfigButton", false);
  toggleDefaultLayout();
  body.classList.add("bg-gray-100");
});

const loadInitialData = async () => {
  try {
    const [rolesResponse, specialtiesResponse] = await Promise.all([
      authService.getDoctorRoles(),
      authService.getSpecialties()
    ]);

    doctorRoles.value = rolesResponse.doctor_roles || [];
    specialties.value = specialtiesResponse.specialties || [];
  } catch (err) {
    console.error("Error loading initial data:", err);
  }
};

const clearFieldError = (field) => {
  if (fieldErrors.value[field]) {
    const updated = { ...fieldErrors.value };
    delete updated[field];
    fieldErrors.value = updated;
  }
};

const clearAllFieldErrors = () => {
  if (Object.keys(fieldErrors.value).length) {
    fieldErrors.value = {};
  }
};

const parseFieldErrors = (errors) => {
  const parsed = {};
  if (!errors || typeof errors !== "object") return parsed;

  Object.entries(errors).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      parsed[key] = value[0];
    } else if (value && typeof value === "object") {
      // En caso de errores anidados
      const nestedValue = Object.values(value)[0];
      parsed[key] = Array.isArray(nestedValue) ? nestedValue[0] : nestedValue;
    } else if (value) {
      parsed[key] = value;
    }
  });

  return parsed;
};

const firstParsedError = (errors) => {
  const values = Object.values(errors || {});
  if (!values.length) return null;
  return Array.isArray(values[0]) ? values[0][0] : values[0];
};

const closeWindow = () => {
  // Intentar cerrar la ventana, pero si no se puede, redirigir al login
  try {
    window.close();
    // Si llegamos aquí, la ventana no se pudo cerrar
    setTimeout(() => {
      window.location.href = '/login';
    }, 1000);
  } catch (error) {
    // Si hay error, redirigir al login
    window.location.href = '/login';
  }
};

const isStepValid = computed(() => {
  switch (currentStep.value) {
    case 0:
      return planCode.value !== ""; // Validar que se haya seleccionado un plan
    case 1:
      return userType.value !== "";
    case 2: {
      // Validar que todos los campos estén llenos
      const basicFieldsValid = name.value && email.value && password.value && passwordConfirmation.value && acceptTerms.value;
      // Validar que las contraseñas coincidan
      const passwordsMatch = password.value === passwordConfirmation.value;

      // Validar que la contraseña cumpla con los criterios (usando el componente validator)
      const passwordMeetsRequirements = passwordValidatorRef.value ? passwordValidatorRef.value.isValid : false;

      return basicFieldsValid && passwordsMatch && passwordMeetsRequirements;
    }
    case 3:
      // Paso final: doctor (profesional + centro) o director (solo centro)
      if (userType.value === "doctor") {
        return doctorInfoComplete.value && centerInfoComplete.value;
      }
      return centerInfoComplete.value;
    default:
      return false;
  }
});

// Computed para verificar si las contraseñas son válidas
const passwordValidation = computed(() => {
  return {
    hasPassword: password.value.length > 0,
    hasConfirmation: passwordConfirmation.value.length > 0,
    passwordsMatch: password.value === passwordConfirmation.value,
    passwordMeetsRequirements: passwordValidatorRef.value ? passwordValidatorRef.value.isValid : false,
    isComplete: password.value === passwordConfirmation.value &&
      (passwordValidatorRef.value ? passwordValidatorRef.value.isValid : false) &&
      password.value.length > 0 && passwordConfirmation.value.length > 0
  };
});

// Computed para especialidades filtradas según el rol del médico
const availableSpecialties = computed(() => {
  if (!doctorRole.value) return [];

  const isGeneralDoctor = isGeneralDoctorRole();
  const isSpecialistDoctor = isSpecialistDoctorRole();
  const generalSpecialtyId = getGeneralSpecialtyId();

  // Solo Medicina General para médicos generales
  if (isGeneralDoctor) {
    return generalSpecialtyId
      ? specialties.value.filter((specialty) => specialty.id === generalSpecialtyId)
      : [];
  }

  // Todas las especialidades excepto Medicina General para especialistas
  if (isSpecialistDoctor) {
    return generalSpecialtyId
      ? specialties.value.filter((specialty) => specialty.id !== generalSpecialtyId)
      : specialties.value;
  }

  return specialties.value;
});

const normalizeRoleValue = (value) => (value ?? "").toString();

const getSelectedDoctorRole = () => {
  const selectedValue = normalizeRoleValue(doctorRole.value);
  return doctorRoles.value.find(
    (role) =>
      normalizeRoleValue(role.id) === selectedValue ||
      normalizeRoleValue(role.slug) === selectedValue ||
      normalizeRoleValue(role.key) === selectedValue
  );
};

const isGeneralDoctorRole = () => {
  const selectedRole = getSelectedDoctorRole();
  return (
    selectedRole &&
    (selectedRole.slug === "general-doctor" ||
      selectedRole.key === "general-doctor" ||
      selectedRole.id === 1 ||
      /general/i.test(selectedRole.name || ""))
  );
};

const isSpecialistDoctorRole = () => {
  const selectedRole = getSelectedDoctorRole();
  return selectedRole ? !isGeneralDoctorRole() : false;
};

const getGeneralSpecialtyId = () => {
  const matchBySlug = specialties.value.find(
    (specialty) =>
      specialty.slug === "medicina-general" ||
      specialty.key === "general-medicine" ||
      specialty.slug === "general-doctor"
  );
  if (matchBySlug) return matchBySlug.id;

  const matchByName = specialties.value.find((specialty) =>
    /general/i.test(specialty.name || "")
  );
  if (matchByName) return matchByName.id;

  // Fallback: asumir id 1 es Medicina General si existe
  const defaultId = specialties.value.find((specialty) => specialty.id === 1);
  return defaultId ? defaultId.id : null;
};

// Watcher para manejar cambios en el rol del médico
const handleDoctorRoleChange = () => {
  const generalSpecialtyId = getGeneralSpecialtyId();

  // Auto-seleccionar Medicina General para médicos generales
  if (isGeneralDoctorRole()) {
    selectedSpecialties.value = generalSpecialtyId ? [generalSpecialtyId] : [];
    return;
  }

  // Limpiar selección para especialistas
  if (isSpecialistDoctorRole()) {
    selectedSpecialties.value = [];
  }
};

const selectUserType = (type) => {
  userType.value = type;
  clearFieldError("user_type");
  nextStep();
};

const goToStep = (stepIndex) => {
  // Solo permitir ir a pasos anteriores o al paso actual
  if (stepIndex <= currentStep.value) {
    currentStep.value = stepIndex;
    error.value = null;
  }
};

const nextStep = () => {
  // Limpiar errores previos
  error.value = null;

  // Validaciones específicas para el paso 2 (contraseñas)
  if (currentStep.value === 2) {
    if (!name.value || !email.value || !password.value || !passwordConfirmation.value) {
      error.value = "Todos los campos son obligatorios.";
      return;
    }

    if (!acceptTerms.value) {
      error.value = "Debes aceptar los términos y condiciones.";
      return;
    }

    if (password.value !== passwordConfirmation.value) {
      error.value = "Las contraseñas no coinciden.";
      return;
    }

    if (passwordValidatorRef.value && !passwordValidatorRef.value.isValid) {
      error.value = "La contraseña no cumple con los requisitos de seguridad.";
      return;
    }
  }

  if (isStepValid.value && currentStep.value < totalSteps.value - 1) {
    currentStep.value++;
  }
};

const prevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
};

const toggleSpecialty = (specialtyId) => {
  // Solo permitir toggle para médicos especialistas
  if (doctorRole.value !== "specialist-doctor") return;

  const index = selectedSpecialties.value.indexOf(specialtyId);
  if (index > -1) {
    selectedSpecialties.value.splice(index, 1);
  } else {
    selectedSpecialties.value.push(specialtyId);
  }
};

const handleRegister = async () => {
  error.value = null;
  successMessage.value = null;
  clearAllFieldErrors();

  // Validaciones
  if (!isStepValid.value) {
    error.value = "Por favor completa todos los campos obligatorios.";
    return;
  }

  if (passwordValidatorRef.value && !passwordValidatorRef.value.isValid) {
    error.value = passwordValidatorRef.value.getValidationMessage();
    return;
  }

  if (password.value !== passwordConfirmation.value) {
    error.value = "Las contraseñas no coinciden.";
    return;
  }

  isLoading.value = true;

  try {
    // Construir objeto de registro según el tipo de usuario
    const registrationData = {
      name: name.value,
      email: email.value,
      password: password.value,
      password_confirmation: password.value,
      user_type: userType.value,
      plan_code: planCode.value, // Campo obligatorio del plan seleccionado
      billing_cycle: billingCycle.value, // mensual o anual
      center: {
        name: centerName.value,
        address: centerAddress.value
      }
    };

    // Agregar datos específicos del médico si aplica
    if (userType.value === "doctor") {
      registrationData.doctor = {
        role: doctorRole.value,
        specialties: selectedSpecialties.value,
        phone: doctorPhone.value,
        email_contact: doctorEmailContact.value || email.value
      };
    }

    const response = await authService.register(registrationData);

    successMessage.value = response.message || "Usuario registrado exitosamente.";

    // Resetear formulario
    resetForm();

    // Ocultar el formulario y mostrar mensaje de éxito
    showForm.value = false;
    successMessage.value = "Se ha enviado un correo de verificación. Por favor, revisa tu bandeja de entrada y haz clic en el enlace de verificación antes de iniciar sesión.";

  } catch (err) {
    const serverErrors = parseFieldErrors(err.response?.data?.errors);
    fieldErrors.value = serverErrors;
    const firstError = firstParsedError(serverErrors);
    error.value = err.response?.data?.message || firstError || "Error al registrar el usuario.";
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  currentStep.value = 0;
  planCode.value = "";
  billingCycle.value = "monthly";
  userType.value = "";
  name.value = "";
  email.value = "";
  password.value = "";
  passwordConfirmation.value = "";
  doctorRole.value = "";
  selectedSpecialties.value = [];
  doctorPhone.value = "";
  doctorEmailContact.value = "";
  centerName.value = "";
  centerAddress.value = "";
  acceptTerms.value = false;
  fieldErrors.value = {};
};

</script>

<template>
  <main class="main-content mt-0">
    <section class="registration-section">
      <div class="page-header min-vh-100">
        <div class="container-fluid">
          <div class="row">
            <!-- Full width container for plans, standard width for other steps -->
            <div class="col-12">
              <div class="card card-plain">
                <!-- Header principal con info -->
                <div v-if="showForm && currentStep === 0" class="main-header text-center mb-4">
                  <h2 class="font-weight-bolder text-gradient mb-2">Registro en iDAMedic</h2>
                  <p class="text-muted mb-0">Crea tu cuenta y comienza tu prueba gratuita</p>
                </div>

                <div v-if="showForm && currentStep > 0" class="card-header pb-0 pt-3">
                  <div class="stepper" :style="{ '--stepper-progress': progressPercentage }">
                    <div class="stepper-track"></div>
                    <div class="stepper-progress"></div>
                    <div class="stepper-steps">
                      <div v-for="(step, index) in stepMeta" :key="step.key" class="stepper-step"
                        :class="{ active: index === currentStep, complete: index < currentStep, clickable: index < currentStep }"
                        @click="index < currentStep ? goToStep(index) : null">
                        <div class="stepper-dot">
                          <i v-if="index < currentStep" class="fas fa-check"></i>
                          <span v-else>{{ index + 1 }}</span>
                        </div>
                        <div class="stepper-text">
                          <span class="stepper-title">{{ step.title }}</span>
                          <span class="stepper-subtitle"
                            :class="{ 'text-success fw-bold': (index === 0 && planCode) || (index === 1 && userType) }">{{
                            step.subtitle }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="stepper-meta">
                      <span class="meta-step">Paso {{ currentStep + 1 }} de {{ totalSteps }}</span>
                      <span class="meta-progress">{{ progressPercentage }}%</span>
                    </div>
                  </div>
                </div>

                <div class="card-body pb-3">

                  <!-- Mensaje de éxito (cuando el registro se completa) -->
                  <div v-if="!showForm" class="text-center py-5">
                    <div class="mb-4">
                      <i class="fas fa-check-circle fa-4x text-success"></i>
                    </div>
                    <h4 class="font-weight-bolder text-success mb-3">¡Registro exitoso!</h4>
                    <p class="text-lg mb-4">{{ successMessage }}</p>
                    <argon-button color="success" class="mb-3" @click="closeWindow">
                      Continuar
                    </argon-button>
                    <p class="text-sm text-muted">
                      Puedes cerrar esta pestaña manualmente o hacer clic en "Continuar".
                    </p>
                  </div>

                  <!-- Formulario de registro (cuando showForm es true) -->
                  <div v-if="showForm">

                    <!-- Step 0: Plan Selection -->
                    <div v-if="currentStep === 0" class="plan-selection-step">
                      <plan-selector v-model="planCode" v-model:billingCycle="billingCycle"
                        :validation-error="fieldErrors.plan_code" @plan-selected="clearFieldError('plan_code')" />

                      <div class="d-flex justify-content-end mt-4">
                        <argon-button color="success" variant="gradient" :disabled="!isStepValid || isLoading"
                          @click="nextStep">
                          Continuar<i class="fas fa-arrow-right ms-2"></i>
                        </argon-button>
                      </div>
                    </div>

                    <!-- Step 1: User Type Selection -->
                    <div v-if="currentStep === 1" class="user-type-selection">
                      <h5 class="text-center mb-4">¿Cómo te registras?</h5>

                      <div class="row g-4">
                        <div class="col-md-6">
                          <div class="user-type-card" :class="{ 'selected': userType === 'doctor' }"
                            @click="selectUserType('doctor')">
                            <div class="card-icon">
                              <i class="fas fa-user-md fa-3x text-success"></i>
                            </div>
                            <h6 class="mt-3 mb-2">Médico</h6>
                            <p class="text-muted small mb-0">
                              Registro para médicos generales y especialistas
                            </p>
                          </div>
                        </div>

                        <div class="col-md-6">
                          <div class="user-type-card" :class="{ 'selected': userType === 'director' }"
                            @click="selectUserType('director')">
                            <div class="card-icon">
                              <i class="fas fa-hospital fa-3x text-success"></i>
                            </div>
                            <h6 class="mt-3 mb-2">Director Médico</h6>
                            <p class="text-muted small mb-0">
                              Registro para directores de centros médicos
                            </p>
                          </div>
                        </div>
                      </div>
                      <small v-if="fieldErrors.user_type" class="text-danger d-block mt-2">{{ fieldErrors.user_type
                      }}</small>
                    </div>

                    <!-- Step 2: Basic Information -->
                    <div v-if="currentStep === 2">
                      <h5 class="text-center mb-4">Información Personal</h5>

                      <div class="info-card">
                        <div class="row g-3 align-items-start">
                          <div class="col-lg-6 col-12">
                            <label for="name" class="form-label">Nombre Completo</label>
                            <argon-input id="name" v-model="name" type="text" placeholder="Dr. Juan Pérez"
                              aria-label="Nombre" @input="clearFieldError('name')" />
                            <small v-if="fieldErrors.name" class="text-danger">{{ fieldErrors.name }}</small>
                          </div>

                          <div class="col-lg-6 col-12">
                            <label for="email" class="form-label">Email</label>
                            <argon-input id="email" v-model="email" type="email" placeholder="juan.perez@ejemplo.com"
                              aria-label="Email" @input="clearFieldError('email')" />
                            <small v-if="fieldErrors.email" class="text-danger">{{ fieldErrors.email }}</small>
                          </div>

                          <div class="col-lg-6 col-12">
                            <label for="password" class="form-label">Contraseña</label>
                            <password-input id="password" v-model="password" placeholder="Contraseña segura"
                              aria-label="Contraseña" :disabled="isLoading" @input="clearFieldError('password')" />
                            <small v-if="fieldErrors.password" class="text-danger">{{ fieldErrors.password }}</small>
                          </div>

                          <div class="col-lg-6 col-12">
                            <label for="passwordConfirmation" class="form-label">Confirmar Contraseña</label>
                            <password-input id="passwordConfirmation" v-model="passwordConfirmation"
                              placeholder="Confirmar contraseña" aria-label="Confirmar Contraseña" :disabled="isLoading"
                              @input="clearFieldError('password_confirmation')" />
                            <small v-if="fieldErrors.password_confirmation" class="text-danger">{{
                              fieldErrors.password_confirmation }}</small>
                          </div>

                          <div class="col-lg-6 col-12">
                            <div class="password-validator-card h-100">
                              <div class="req-title mb-2">La contraseña debe contener:</div>
                              <password-validator ref="passwordValidatorRef" :password="password" />
                              <div v-if="password && passwordConfirmation" class="password-match-indicator mt-3">
                                <div v-if="password === passwordConfirmation" class="alert alert-success py-2 mb-0">
                                  <i class="fas fa-check-circle me-2"></i>
                                  Las contraseñas coinciden
                                </div>
                                <div v-else class="alert alert-danger py-2 mb-0">
                                  <i class="fas fa-times-circle me-2"></i>
                                  Las contraseñas no coinciden
                                </div>
                              </div>
                            </div>
                          </div>

                          <div class="col-lg-6 col-12">
                            <div class="password-validator-card h-100">
                              <div class="req-title mb-2">Estado de validación</div>
                              <div class="validation-summary">
                                <div :class="name && email ? 'text-success' : 'text-muted'">
                                  <i :class="name && email ? 'fas fa-check' : 'fas fa-circle'"></i>
                                  Información personal completa
                                </div>
                                <div
                                  :class="passwordValidation.passwordMeetsRequirements ? 'text-success' : 'text-muted'">
                                  <i
                                    :class="passwordValidation.passwordMeetsRequirements ? 'fas fa-check' : 'fas fa-circle'"></i>
                                  Contraseña cumple requisitos
                                </div>
                                <div
                                  :class="passwordValidation.passwordsMatch && passwordValidation.hasPassword && passwordValidation.hasConfirmation ? 'text-success' : 'text-muted'">
                                  <i
                                    :class="passwordValidation.passwordsMatch && passwordValidation.hasPassword && passwordValidation.hasConfirmation ? 'fas fa-check' : 'fas fa-circle'"></i>
                                  Contraseñas coinciden
                                </div>
                                <div :class="acceptTerms ? 'text-success' : 'text-muted'">
                                  <i :class="acceptTerms ? 'fas fa-check' : 'fas fa-circle'"></i>
                                  Términos aceptados
                                </div>
                              </div>
                            </div>
                          </div>

                          <div class="col-12">
                            <div class="form-check d-flex align-items-center gap-2">
                              <input id="acceptTerms" v-model="acceptTerms" class="form-check-input" type="checkbox" />
                              <label class="form-check-label mb-0 d-flex align-items-center flex-wrap gap-1"
                                for="acceptTerms">
                                <span>Acepto los</span>
                                <a href="https://idamedic.me/terminos-condiciones-soporte/" target="_blank"
                                  rel="noopener" class="text-success fw-bold">
                                  términos y condiciones
                                </a>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="d-flex justify-content-between mt-4">
                        <argon-button color="light" :disabled="isLoading" @click="prevStep">
                          <i class="fas fa-arrow-left me-2"></i>Anterior
                        </argon-button>
                        <argon-button color="success" variant="gradient" :disabled="!isStepValid || isLoading"
                          @click="nextStep">
                          Siguiente<i class="fas fa-arrow-right ms-2"></i>
                        </argon-button>
                      </div>

                    </div>

                    <!-- Step 3: Professional Information -->
                    <!-- Step 3: Doctor info (only for doctors) -->
                    <div v-if="isDoctorInfoStep">
                      <h5 class="text-center mb-4">Información Profesional</h5>

                      <div class="info-card">
                        <div class="field-grid">
                          <div class="field-cell">
                            <label for="centerName" class="form-label">Nombre del Centro</label>
                            <argon-input id="centerName" v-model="centerName" type="text"
                              :placeholder="userType === 'doctor' ? 'Consultorio Dr. Juan Pérez' : 'Clínica San José'"
                              aria-label="Nombre del Centro" @input="clearFieldError('center_name')" />
                            <small v-if="fieldErrors.center_name" class="text-danger">{{ fieldErrors.center_name
                              }}</small>
                          </div>

                          <div class="field-cell">
                            <label for="centerAddress" class="form-label">Dirección</label>
                            <argon-input id="centerAddress" v-model="centerAddress" type="text"
                              placeholder="Av. Revolución 123, Col. Centro, Ciudad de México" aria-label="Dirección"
                              @input="clearFieldError('center_address')" />
                            <small v-if="fieldErrors.center_address" class="text-danger">{{ fieldErrors.center_address
                            }}</small>
                          </div>

                          <div class="field-cell">
                            <label for="doctorPhone" class="form-label">Teléfono de Contacto</label>
                            <argon-input id="doctorPhone" v-model="doctorPhone" type="tel"
                              placeholder="+52 123 456 7890" aria-label="Teléfono"
                              @input="clearFieldError('doctor_phone')" />
                            <small v-if="fieldErrors.doctor_phone" class="text-danger">{{ fieldErrors.doctor_phone
                            }}</small>
                          </div>

                          <div class="field-cell">
                            <label for="doctorRole" class="form-label">Tipo de Médico</label>
                            <select id="doctorRole" v-model="doctorRole" class="form-select"
                              @change="handleDoctorRoleChange(); clearFieldError('doctorRole'); clearFieldError('doctor.role')">
                              <option value="">Selecciona tu rol</option>
                              <option v-for="role in doctorRoles" :key="role.id" :value="role.id">
                                {{ role.name }}
                              </option>
                            </select>
                            <small v-if="fieldErrors.doctorRole" class="text-danger">{{ fieldErrors.doctorRole
                              }}</small>
                          </div>

                          <div v-if="doctorRole" class="field-cell field-span-full">
                            <label class="form-label">Especialidades</label>

                            <!-- Para Médico General - Solo mostrar Medicina General -->
                            <div v-if="isGeneralDoctorRole()" class="specialty-display">
                              <div class="specialty-item selected disabled">
                                <i class="fas fa-check"></i>
                                Medicina General
                              </div>
                              <small class="text-muted d-block mt-2">Como médico general, tu especialidad es Medicina
                                General</small>
                            </div>

                            <!-- Para Médico Especialista - Permitir selección múltiple -->
                            <div v-else class="specialties-selection">
                              <div class="specialties-grid">
                                <div v-for="specialty in availableSpecialties" :key="specialty.id"
                                  class="specialty-item"
                                  :class="{ 'selected': selectedSpecialties.includes(specialty.id) }"
                                  @click="toggleSpecialty(specialty.id); clearFieldError('specialties'); clearFieldError('doctor.specialties')">
                                  <i v-if="selectedSpecialties.includes(specialty.id)" class="fas fa-check"></i>
                                  {{ specialty.name }}
                                </div>
                              </div>
                              <small class="text-muted d-block mt-2">Selecciona una o más especialidades médicas</small>
                              <small v-if="fieldErrors.specialties || fieldErrors['doctor.specialties']"
                                class="text-danger d-block">
                                {{ fieldErrors.specialties || fieldErrors['doctor.specialties'] }}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="d-flex justify-content-between mt-4">
                        <argon-button color="light" :disabled="isLoading" @click="prevStep">
                          <i class="fas fa-arrow-left me-2"></i>Anterior
                        </argon-button>
                        <argon-button color="success" variant="gradient" :disabled="!isStepValid || isLoading"
                          @click="isLastStep ? handleRegister() : nextStep()">
                          {{ isLastStep ? 'Completar Registro' : 'Siguiente' }}<i
                            :class="isLastStep ? 'fas fa-check ms-2' : 'fas fa-arrow-right ms-2'"></i>
                        </argon-button>
                      </div>
                    </div>

                    <!-- Step 3 (director) or 4 (doctor): Center information -->
                    <div v-if="isCenterInfoStep">
                      <h5 class="text-center mb-4">
                        {{ userType === 'doctor' ? 'Información del Consultorio/Clínica' : 'Información del Centro Médico' }}
                      </h5>

                      <div class="info-card">
                        <div class="field-grid">
                          <div class="field-cell">
                            <label for="centerName" class="form-label">Nombre del Centro</label>
                            <argon-input id="centerName" v-model="centerName" type="text"
                              :placeholder="userType === 'doctor' ? 'Consultorio Dr. Juan Pérez' : 'Clínica San José'"
                              aria-label="Nombre del Centro" @input="clearFieldError('center_name')" />
                            <small v-if="fieldErrors.center_name" class="text-danger">{{ fieldErrors.center_name
                              }}</small>
                          </div>

                          <div class="field-cell">
                            <label for="centerAddress" class="form-label">Dirección</label>
                            <argon-input id="centerAddress" v-model="centerAddress" type="text"
                              placeholder="Av. Revolución 123, Col. Centro, Ciudad de México" aria-label="Dirección"
                              @input="clearFieldError('center_address')" />
                            <small v-if="fieldErrors.center_address" class="text-danger">{{ fieldErrors.center_address
                            }}</small>
                          </div>

                        </div>
                      </div>

                      <div class="d-flex justify-content-between mt-4">
                        <argon-button color="light" :disabled="isLoading" @click="prevStep">
                          <i class="fas fa-arrow-left me-2"></i>Anterior
                        </argon-button>
                        <argon-button color="success" variant="gradient" :disabled="!isStepValid || isLoading"
                          @click="isLastStep ? handleRegister() : nextStep()">
                          {{ isLastStep ? (isLoading ? "Registrando..." : "Completar Registro") : "Siguiente" }}
                          <i :class="isLastStep ? 'fas fa-check ms-2' : 'fas fa-arrow-right ms-2'"></i>
                        </argon-button>
                      </div>
                    </div>

                    <!-- Mensajes de error y éxito -->
                    <ErrorAlert :error="error" type="danger" />
                    <ErrorAlert :error="successMessage" type="success" />

                  </div> <!-- Cierre del div showForm -->
                </div>

                <div v-if="showForm" class="card-footer text-center pt-0 px-sm-4 px-1">
                  <p class="mx-auto mb-4 text-sm">
                    ¿Ya tienes una cuenta?
                    <router-link :to="{ name: 'Login' }" class="text-success text-gradient font-weight-bold">
                      Iniciar sesión
                    </router-link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>

</template>

<style scoped>
.registration-section {
  background: white;
  min-height: 100vh;
  position: relative;
}

.registration-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 0;
  background: transparent;
  pointer-events: none;
}

.page-header {
  position: relative;
  z-index: 1;
  padding: 1.5rem 0 2.5rem;
}

.card-plain {
  background: white;
  border-radius: 18px;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.12);
  padding: 2rem 1.75rem 2.25rem;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
}

.main-header {
  padding: 2rem 1rem 1.5rem;
  border-bottom: 2px solid rgba(45, 206, 137, 0.1);
}

.main-header h2 {
  font-size: 2.2rem;
  margin-bottom: 0.75rem;
}

.trial-info {
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #f0fdf7 0%, #ffffff 100%);
  border: 2px solid rgba(45, 206, 137, 0.2);
  border-radius: 50px;
  color: #2dce89;
  font-size: 1rem;
  margin-top: 0.5rem;
}

.trial-info i {
  font-size: 1.2rem;
}

.text-gradient {
  background: linear-gradient(135deg, #2dce89 0%, #5e72e4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.stepper {
  --stepper-progress: 0;
  --stepper-gutter: 18px;
  position: relative;
  max-width: 1260px;
  margin: 0 auto;
  padding: 1.5rem 0 1rem;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-radius: 16px;
}

.stepper-track,
.stepper-progress {
  position: absolute;
  top: 38px;
  left: var(--stepper-gutter);
  height: 3px;
  border-radius: 999px;
}

.stepper-track {
  right: var(--stepper-gutter);
  background: linear-gradient(90deg, #e2e8f0 0%, #cbd5e1 100%);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
}

.stepper-progress {
  width: calc((100% - (var(--stepper-gutter) * 2)) * var(--stepper-progress) / 100);
  background: linear-gradient(90deg, #2dce89 0%, #5eead4 100%);
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(45, 206, 137, 0.3);
}

.stepper-steps {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.5rem;
  position: relative;
  z-index: 2;
  padding: 0 0.5rem;
}

.stepper-step {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}

.stepper-step.clickable {
  cursor: pointer;
}

.stepper-step.clickable:hover .stepper-dot {
  transform: scale(1.1);
  box-shadow: 0 8px 24px rgba(45, 206, 137, 0.4);
}

.stepper-step.clickable:hover .stepper-title {
  color: #2dce89;
}

.stepper-dot {
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 2px solid #e2e8f0;
  background: white;
  color: #94a3b8;
  font-weight: 700;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.stepper-step.complete .stepper-dot {
  background: linear-gradient(135deg, #2dce89 0%, #5eead4 100%);
  border-color: #2dce89;
  color: #fff;
  box-shadow: 0 6px 20px rgba(45, 206, 137, 0.35);
  transform: scale(1.05);
}

.stepper-step.active .stepper-dot {
  border-color: #2dce89;
  color: #2dce89;
  background: linear-gradient(135deg, #ecfdf3 0%, #ffffff 100%);
  box-shadow: 0 6px 20px rgba(45, 206, 137, 0.2);
  transform: scale(1.08);
}

.stepper-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  padding-top: 0.5rem;
}

.stepper-title {
  font-weight: 700;
  color: #1f2937;
  font-size: 0.95rem;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.stepper-step.active .stepper-title {
  color: #2dce89;
}

.stepper-subtitle {
  font-size: 0.75rem;
  color: #94a3b8;
  transition: all 0.3s ease;
  line-height: 1.3;
  font-weight: 500;
}

.stepper-subtitle.text-success {
  color: #2dce89 !important;
  font-weight: 600;
}

.stepper-meta {
  display: flex;
  justify-content: space-between;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(148, 163, 184, 0.15);
}

.meta-step,
.meta-progress {
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.02em;
}

.meta-progress {
  background: linear-gradient(135deg, #2dce89 0%, #5eead4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem 1.5rem;
  align-items: start;
  width: 100%;
  justify-items: stretch;
}

.field-cell {
  min-width: 0;
  width: 100%;
}

.field-span-full {
  grid-column: 1 / -1;
}

.field-grid :deep(.form-group),
.field-grid :deep(.form-control),
.field-grid :deep(.input-group) {
  width: 100%;
  max-width: none;
}

.field-grid :deep(.form-group > div) {
  width: 100%;
}

.field-grid .form-select {
  width: 100%;
  max-width: none;
}

.user-type-card {
  padding: 3rem 2rem;
  border: 2px solid #e9ecef;
  border-radius: 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  padding: 2.5rem;
  max-width: 1100px;
  margin: 0 auto 1.5rem;
  box-shadow: 0 20px 50px rgba(50, 50, 93, 0.08);

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  min-height: 240px;
}

.user-type-card:hover {
  border-color: #82d616;
  box-shadow: 0 8px 30px rgba(130, 214, 22, 0.15);

  transform: translateY(-4px);
}

.user-type-card.selected {
  border-color: #82d616;

  background: rgba(130, 214, 22, 0.05);
  box-shadow: 0 8px 30px rgba(130, 214, 22, 0.2);
}

.card-icon {
  margin-bottom: 1rem;
}

.info-card {
  background: white;

  border: 2px solid #e9ecef;
  border-radius: 1rem;
  padding: 2rem;
  margin-bottom: 1rem;
  width: 100%;
  max-width: none;
  margin-left: auto;
  margin-right: auto;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.06);
}

.password-validator-card {
  background: #f8fafc;
  border: 1px solid #e9ecef;
  border-radius: 0.75rem;
  padding: 1.25rem 1.5rem;
}

.req-title {
  font-weight: 700;
  color: #32425d;
}


.form-select {
  display: block;
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.4rem;
  color: #495057;
  background-color: #fff;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 16px 12px;
  border: 1px solid #d2d6da;
  border-radius: 0.5rem;
  appearance: none;
}

.form-select:focus {
  border-color: #82d616;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(130, 214, 22, 0.25);
}

.specialties-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.specialty-item {
  padding: 0.75rem 1rem;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  font-size: 0.875rem;
  position: relative;
}

.specialty-item:hover {
  border-color: #82d616;
  background-color: rgba(130, 214, 22, 0.05);
}

.specialty-item.selected {
  border-color: #82d616;
  background-color: rgba(130, 214, 22, 0.1);
  color: #82d616;
  font-weight: 600;
}

.specialty-item.selected .fas {
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  font-size: 0.75rem;
}

.specialty-item.disabled {
  cursor: not-allowed;
  opacity: 0.8;
}

.specialty-display {
  margin-bottom: 0.5rem;
}

.specialties-selection {
  margin-bottom: 0.5rem;
}

.password-match-indicator {
  font-size: 0.875rem;
  font-weight: 500;
}

.password-match-indicator .text-success {
  color: #28a745 !important;
}

.password-match-indicator .text-danger {
  color: #dc3545 !important;
}

.password-match-indicator .alert.alert-danger {
  background: #f5365c;
  color: #fff;
  border-color: #f5365c;
}

.password-match-indicator .alert.alert-danger i {
  color: #fff;
}

.validation-summary {
  font-size: 0.75rem;
  line-height: 1.4;
}

.validation-summary div {
  margin-bottom: 0.25rem;
}

.validation-summary i {
  width: 12px;
  margin-right: 0.5rem;
}

.form-check .form-check-label {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.center-info {
  border-top: 1px solid #e9ecef;
  padding-top: 1.5rem;
  margin-top: 1.5rem;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  padding: 2rem 2.5rem 2rem 2.5rem;
  min-width: 320px;
  max-width: 90vw;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.2);
}

.modal-body {
  display: flex;
  flex-direction: column;
  align-items: center;
}

@media (max-width: 992px) {
  .stepper-steps {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .stepper-track,
  .stepper-progress {
    top: 20px;
  }

  .field-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .specialties-grid {
    grid-template-columns: 1fr;
  }

  .card-plain {
    padding: 1.5rem 1.25rem 1.75rem;
  }

  .user-type-card {
    padding: 1.5rem;
  }

  .stepper {
    padding-top: 0;
  }

  .stepper-steps {
    grid-template-columns: 1fr;
  }

  .stepper-track,
  .stepper-progress {
    display: none;
  }

  .stepper-step {
    padding: 0.5rem 0;
    border-bottom: 1px dashed rgba(148, 163, 184, 0.35);
  }

  .stepper-step:last-child {
    border-bottom: none;
  }
}
</style>
